import type { Conversation } from './types';

export type ConversationTimeSection = 'today' | 'yesterday' | 'last7' | 'last30';

export const CONVERSATION_SECTION_LABELS: Record<ConversationTimeSection, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  last7: 'Last 7 Days',
  last30: 'Last 30 Days',
};

const SECTION_ORDER: ConversationTimeSection[] = ['today', 'yesterday', 'last7', 'last30'];

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function daysBefore(date: Date, days: number): Date {
  const next = startOfLocalDay(date);
  next.setDate(next.getDate() - days);
  return next;
}

export function sectionForUpdatedAt(
  updatedAt: string,
  now: Date = new Date(),
): ConversationTimeSection {
  const updated = new Date(updatedAt);
  const todayStart = startOfLocalDay(now);
  const yesterdayStart = daysBefore(now, 1);
  const last7Start = daysBefore(now, 7);

  if (updated >= todayStart) {
    return 'today';
  }
  if (updated >= yesterdayStart) {
    return 'yesterday';
  }
  if (updated >= last7Start) {
    return 'last7';
  }
  return 'last30';
}

export function groupConversationsByTime(
  rows: Conversation[],
  now: Date = new Date(),
): Array<{ section: ConversationTimeSection; conversations: Conversation[] }> {
  const buckets = new Map<ConversationTimeSection, Conversation[]>();
  for (const section of SECTION_ORDER) {
    buckets.set(section, []);
  }

  for (const conversation of rows) {
    const section = sectionForUpdatedAt(conversation.updatedAt, now);
    buckets.get(section)!.push(conversation);
  }

  return SECTION_ORDER.flatMap((section) => {
    const conversations = buckets.get(section)!;
    if (conversations.length === 0) {
      return [];
    }
    conversations.sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
    return [{ section, conversations }];
  });
}
